import { FC, Fragment, ReactNode } from 'react';

import { Box, Card, Divider, Heading, Stack, Text } from '@eneco/sparky';

type HeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type FeatureListItem = {
  /** Leading icon element, e.g. `<ClockIcon color="iconBrand" />`. */
  icon: ReactNode;
  /** Bold title shown next to the icon. */
  title: ReactNode;
  /** Supporting copy shown beneath the title. */
  description?: ReactNode;
};

export type FeatureListProps = {
  /** The features to render, top to bottom. */
  items: FeatureListItem[];
  /** Semantic heading level used for each item title. Defaults to `h3`. */
  titleAs?: HeadingLevel;
  /** Render a divider between items. Defaults to `true`. */
  withDividers?: boolean;
};

const FeatureListRow: FC<FeatureListItem & { titleAs: HeadingLevel }> = ({ icon, title, description, titleAs }) => (
  <Stack as="li" direction="row" gap="4" alignY="start">
    <Stack.Item shrink={false}>{icon}</Stack.Item>
    <Stack gap="1">
      <Heading as={titleAs} size="3XS">
        {title}
      </Heading>
      {description ? (
        <Text size="BodyS" color="textLowEmphasis">
          {description}
        </Text>
      ) : null}
    </Stack>
  </Stack>
);

/**
 * Presentational list of features/benefits, each with a leading icon, a bold title and optional
 * supporting copy. Icons are supplied by the consumer so any Sparky icon (or custom element) can be
 * used. By default the list is wrapped in a rounded, elevated card with dividers between items.
 */
export const FeatureList: FC<FeatureListProps> = ({ items, titleAs = 'h4', withDividers = true }) => {
  return (
    <Card corners="rounded" elevation="S">
      <Box paddingY="3" paddingX="4">
        <Stack as="ul" gap="3">
          {items.map((item, index) => (
            <Fragment key={index}>
              {withDividers && index > 0 ? <Divider /> : null}
              <FeatureListRow {...item} titleAs={titleAs} />
            </Fragment>
          ))}
        </Stack>
      </Box>
    </Card>
  );
};

export default FeatureList;
